import { useCallback, useEffect, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import {
  accountantApi,
  EXPORT_DATASETS,
  type AccountingPeriod,
  type ChecklistItem,
} from '../api/accountant'
import { ExportApprovalRequiredError, saveExportFile } from '../api/exports'
import { useFeatureFlag } from '../lib/featureFlags'
import { ExportApprovalDialog } from '../components/ExportApprovalDialog'
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  DataTable,
  FilterBar,
  Icon,
  List,
  ListItem,
  LoadingState,
  Page,
  PageHeader,
  PaneLayout,
  StatusPill,
  Tabs,
  TextField,
  ExportMenu,
  type ExportFormat,
} from '../components/ui'

/**
 * F-069 (TASK-0323, REQ-191-001 / REQ-181-001) — area del commercialista: export CSV e chiusura del mese.
 *
 * <b>Export.</b> Solo CSV (separatore ";", date gg/mm/aaaa, decimali con la virgola, UTF-8). Spese, fatture e
 * movimenti dei soci contengono importi: è l'unica eccezione alla regola "nessun importo nei nuovi output",
 * limitata a chi ha il ruolo amministrativo. Mai IBAN, note libere o dati dei clienti.
 *
 * <b>Chiusura periodo.</b> La checklist è a conferma MANUALE: riconciliazioni e imposte non esistono ancora nel
 * sistema, quindi nessuna voce è calcolata. Il mese si chiude solo con tutte le voci spuntate o dichiarate non
 * applicabili (con nota). Dopo la chiusura le SPESE del mese non si modificano; fatture, provvigioni e
 * movimenti dei soci non sono bloccati. La riapertura è riservata all'Admin e richiede un motivo.
 */

const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

function formatDateTime(s?: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleString('it-IT')
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

export function AccountantPage({ auth }: { auth: LoginResponse }) {
  const [tab, setTab] = useState<'export' | 'periods'>('export')

  return (
    <Page>
      <PageHeader title="Commercialista" icon="calculate" />

      <Tabs
        aria-label="Sezioni dell'area commercialista"
        value={tab}
        onChange={id => setTab(id as 'export' | 'periods')}
        items={[
          { id: 'export', label: 'Export' },
          { id: 'periods', label: 'Chiusure periodo' },
        ]}
      />

      {tab === 'export' ? <ExportTab /> : <PeriodsTab canReopen={isAdminRole(auth)} />}
    </Page>
  )
}

/* ── Export ──────────────────────────────────────────────────────────────────────────────────── */

/** Voce dell'elenco documenti: non e' un dataset di `/api/finance/exports` ma `/api/finance/documents/export`. */
const DOCUMENTS_ID = 'documenti-finanza'

function ExportTab() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [approval, setApproval] = useState<{ id: string; label: string } | null>(null)
  const excelOn = useFeatureFlag('export.excel')

  async function download(id: string, format: ExportFormat = 'csv') {
    setBusy(`${id}:${format}`)
    setError('')
    try {
      saveExportFile(
        id === DOCUMENTS_ID
          ? await accountantApi.exportDocuments(from || undefined, to || undefined, format)
          : await accountantApi.exportCsv(id, from || undefined, to || undefined, format),
      )
    } catch (e) {
      // F-117 (REQ-266-001): l'export richiede l'approvazione di un amministratore: si apre la richiesta.
      if (e instanceof ExportApprovalRequiredError) {
        setApproval({ id, label: id === DOCUMENTS_ID ? 'Elenco documenti' : (EXPORT_DATASETS.find(d => d.id === id)?.label ?? id) })
        return
      }
      setError(errMsg(e, 'Errore durante l\'export'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <Callout tone="info">
        File CSV (separatore «;», date gg/mm/aaaa, decimali con la virgola). Il periodo si applica ai dataset marcati
        «filtrato per periodo» (spese, fatture, movimenti dei soci, movimenti bancari, riconciliazioni, scadenze, budget);
        le anagrafiche si esportano per intero. Gli IBAN e le note libere non sono mai inclusi. Ogni download è registrato.
      </Callout>
      <FilterBar>
        <TextField label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField label="Al" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      <Card padding="none">
        <List aria-label="Dataset esportabili">
          {EXPORT_DATASETS.map(d => (
            <ListItem
              key={d.id}
              headline={d.label}
              supporting={d.dated ? 'filtrato per periodo' : undefined}
              trailing={
<ExportMenu size="sm" variant="text" busy={busy?.startsWith(`${d.id}:`) ?? false} disabled={busy !== null} formats={excelOn ? ['csv', 'xlsx', 'pdf'] : ['csv']} onExport={f => void download(d.id, f)} />
              }
            />
          ))}
          {/* F-140 (REQ-191-001): l'elenco dei documenti dell'area finanza (solo metadati, mai i file). */}
          <ListItem
            headline="Elenco documenti"
            supporting="filtrato per periodo · solo l’elenco, non i file"
            trailing={
<ExportMenu size="sm" variant="text" busy={busy?.startsWith(`${DOCUMENTS_ID}:`) ?? false} disabled={busy !== null} formats={excelOn ? ['csv', 'xlsx', 'pdf'] : ['csv']} onExport={f => void download(DOCUMENTS_ID, f)} />
            }
          />
        </List>
      </Card>
      {approval && (
        <ExportApprovalDialog
          dataset={`finance:${approval.id}`}
          datasetLabel={approval.label}
          filters={{ ...(from ? { from } : {}), ...(to ? { to } : {}) }}
          onClose={() => setApproval(null)}
        />
      )}
    </>
  )
}

/* ── Chiusure periodo ────────────────────────────────────────────────────────────────────────── */

function PeriodsTab({ canReopen }: { canReopen: boolean }) {
  const [year, setYear] = useState(new Date().getFullYear())
  const [periods, setPeriods] = useState<AccountingPeriod[]>([])
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setPeriods((await accountantApi.periods(year)).items)
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento dei periodi'))
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { void load() }, [load])

  const selected = periods.find(p => p.month === selectedMonth) ?? null

  return (
    <>
      <FilterBar>
        <TextField
          label="Anno"
          type="number"
          className="md-u-w-112"
          value={year}
          min={2000}
          max={2200}
          onChange={e => { setYear(Number(e.target.value) || new Date().getFullYear()); setSelectedMonth(null) }}
        />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}
      {loading && periods.length === 0 && <LoadingState />}

      <PaneLayout
        variant="list-detail"
        paneLabel="Mesi contabili"
        mainLabel="Checklist di chiusura"
        pane={
          <Card padding="none">
              <DataTable variant="plain">
                <thead>
                  <tr><th className="ps-3">Mese</th><th>Stato</th><th className="text-end pe-3">Voci da confermare</th></tr>
                </thead>
                <tbody>
                  {periods.map(p => (
                    <tr
                      key={p.month}
                      aria-selected={p.month === selectedMonth}
                      className="md-u-pointer"
                      onClick={() => setSelectedMonth(p.month)}
                    >
                      <td className="ps-3">{MONTHS[p.month - 1]}</td>
                      <td>
                        <StatusPill tone={p.status === 'Chiuso' ? 'primary' : 'neutral'}>{p.status}</StatusPill>
                      </td>
                      <td className="text-end pe-3 md-num">{p.status === 'Chiuso' ? '—' : p.pendingCount}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>
        }
        main={selected
          ? <PeriodDetail key={`${selected.year}-${selected.month}`} period={selected} canReopen={canReopen} onChanged={load} />
          : <Callout tone="info">Seleziona un mese per vedere la checklist di chiusura.</Callout>}
      />
    </>
  )
}

function PeriodDetail({ period, canReopen, onChanged }: {
  period: AccountingPeriod
  canReopen: boolean
  onChanged: () => Promise<void>
}) {
  const closed = period.status === 'Chiuso'
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [reason, setReason] = useState('')

  async function close() {
    setBusy(true)
    setError('')
    try {
      await accountantApi.close(period.year, period.month)
      await onChanged()
    } catch (e) {
      setError(errMsg(e, 'Errore durante la chiusura'))
    } finally {
      setBusy(false)
    }
  }

  async function reopen() {
    setBusy(true)
    setError('')
    try {
      await accountantApi.reopen(period.year, period.month, reason)
      setReason('')
      await onChanged()
    } catch (e) {
      setError(errMsg(e, 'Errore durante la riapertura'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title={`${MONTHS[period.month - 1]} ${period.year}`}
        actions={<StatusPill tone={closed ? 'primary' : 'neutral'}>{period.status}</StatusPill>}
      />
      <CardContent>
        {closed && (
          <p className="md-typescale-body-small text-muted">
            Chiuso il {formatDateTime(period.closedAt)} (utente {period.closedByUserId ?? '—'}). Le spese di questo mese non si
            modificano; fatture, provvigioni e movimenti dei soci restano modificabili.
          </p>
        )}
        {!closed && period.reopenedAt && (
          <p className="md-typescale-body-small text-muted">Riaperto il {formatDateTime(period.reopenedAt)}: {period.reopenReason}</p>
        )}

        {error && <Callout tone="error">{error}</Callout>}

        <List aria-label="Checklist di chiusura" className="mb-3">
          {period.checklist.map(item => (
            <ChecklistRow key={item.code} period={period} item={item} disabled={closed} onChanged={onChanged} />
          ))}
        </List>

        {!closed && (
          <Button variant="filled" disabled={busy} onClick={() => void close()}>
            Chiudi mese
          </Button>
        )}
        {!closed && period.pendingCount > 0 && (
          <span className="md-typescale-body-small text-muted ms-2">Restano {period.pendingCount} voci da confermare.</span>
        )}

        {closed && canReopen && (
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div className="flex-grow-1">
              <TextField label="Motivo della riapertura" value={reason} onChange={e => setReason(e.target.value)} />
            </div>
            <Button variant="outlined" disabled={busy || reason.trim() === ''} onClick={() => void reopen()}>
              Riapri mese
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ChecklistRow({ period, item, disabled, onChanged }: {
  period: AccountingPeriod
  item: ChecklistItem
  disabled: boolean
  onChanged: () => Promise<void>
}) {
  const [checked, setChecked] = useState(item.checked && !item.nonApplicabile)
  const [nonApplicabile, setNonApplicabile] = useState(item.nonApplicabile)
  const [note, setNote] = useState(item.note ?? '')
  const [message, setMessage] = useState('')
  const [saving, setSaving] = useState(false)

  const resolved = item.checked || item.nonApplicabile

  async function save() {
    setSaving(true)
    setMessage('')
    try {
      await accountantApi.setItem(period.year, period.month, item.code, {
        checked: checked || nonApplicabile,
        note: note.trim() || undefined,
        nonApplicabile,
      })
      await onChanged()
    } catch (e) {
      setMessage(errMsg(e, 'Errore'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ListItem headline={<div className="w-100">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <Icon
          name={resolved ? 'check_circle' : 'radio_button_unchecked'}
          size={20}
          className={resolved ? 'text-success' : 'text-muted'}
        />
        <span className="md-typescale-emphasized-body-medium me-auto">{item.label}</span>
        <Checkbox label="Confermata" disabled={disabled || nonApplicabile} checked={checked || nonApplicabile} onChange={e => setChecked(e.target.checked)} />
        <Checkbox label="Non applicabile" disabled={disabled} checked={nonApplicabile} onChange={e => { setNonApplicabile(e.target.checked); if (e.target.checked) setChecked(false) }} />
      </div>
      <div className="d-flex gap-2 mt-1 align-items-center">
        <div className="flex-grow-1">
          <TextField
            label={nonApplicabile ? 'Nota obbligatoria' : 'Nota (facoltativa)'}
            disabled={disabled}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </div>
        {!disabled && (
          <Button size="sm" variant="text" disabled={saving} onClick={() => void save()}>Salva</Button>
        )}
      </div>
      {item.checkedAt && <div className="md-typescale-body-small text-muted mt-1">Confermata il {formatDateTime(item.checkedAt)} (utente {item.checkedByUserId ?? '—'})</div>}
      {message && <div className="md-typescale-body-small text-danger mt-1">{message}</div>}
    </div>} />
  )
}
